import React, { useState } from 'react'
import { connect } from 'react-redux'
import {
  Modal,
  Card,
  Form,
  Input,
  Button,
  Table,
  Row,
  message,
} from 'antd'
import _ from 'lodash'
import { useHistory } from 'react-router-dom'
import serviceInterface from '../../services/service.interface'
import { createNewCase } from '../../store/action/actioncases.action'
import { constants } from '../../constants'

// Compose a message to any patient in the company, sent to the patient as
// "MoveAI Health Support" (a 'MoveAI Message' case, so the conversation lives
// in the Cases list). Search by last name, last 4 digits of phone, or full
// phone number; results show the patient's clinic.
const MoveAIMessageModal = (props) => {
  const history = useHistory()
  const [searchText, setSearchText] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState(null)
  const [selected, setSelected] = useState(null)
  const [sending, setSending] = useState(false)
  const [form] = Form.useForm()

  const reset = () => {
    setSearchText('')
    setResults(null)
    setSelected(null)
    form.resetFields()
  }

  const handleCancel = () => {
    reset()
    props.onClose()
  }

  // Digits-only input searches by phone (4 digits = last-4, more = full
  // number); anything else searches by last name.
  const buildFilters = (text) => {
    const cleaned = text.replace(/[\s()+.-]/g, '')
    if (/^\d+$/.test(cleaned) && cleaned.length >= 4) {
      if (cleaned.length == 4) return { lastdigitsofphone: Number(cleaned) }
      return { phone: cleaned }
    }
    return { lastname_plain: text.trim() }
  }

  const searchPatients = async (filters) => {
    const data = await serviceInterface.PatientService().getPatientListForCompany({
      companyid: props.companyid,
      metadata: { current: 1, pageSize: 1000 },
      filters,
      sorter: {},
    })
    return data && data.patients ? data.patients : []
  }

  const handleSearch = async () => {
    const text = searchText.trim()
    if (!text) return
    setSearching(true)
    setSelected(null)
    try {
      const filters = buildFilters(text)
      let patients = await searchPatients(filters)
      // Full US numbers are stored with the country code — retry with a
      // leading 1 when a 10-digit search comes back empty.
      if (_.isEmpty(patients) && filters.phone && filters.phone.length == 10) {
        patients = await searchPatients({ phone: '1' + filters.phone })
      }
      setResults(patients)
    } catch (err) {
      console.log(err)
      message.error('Patient search failed')
    }
    setSearching(false)
  }

  const handleSend = () => {
    form.validateFields().then((values) => {
      if (!selected) return
      setSending(true)
      props
        .createNewCase({
          patientId: selected._id,
          description: values.message,
          summary: values.subject,
          type: constants.ACTION_CASE_TITLE.MOVEAI_MESSAGE,
          messageType: 'Conversation',
        })
        .then((res) => {
          setSending(false)
          if (res.status == constants.MESSAGE.SUCCESS) {
            message.success(
              'Message sent from ' + constants.MOVEAI_SUPPORT_NAME + '.'
            )
            reset()
            props.onClose()
            if (res.caseid) history.push('/cases/' + res.caseid)
          } else {
            message.error(res.message || 'Failed to send message')
          }
        })
        .catch(() => {
          setSending(false)
          message.error('Failed to send message')
        })
    })
  }

  const columns = [
    {
      title: 'Patient',
      key: 'name',
      render: (r) =>
        _.upperFirst(r.firstname) + ' ' + _.upperFirst(r.lastname),
    },
    {
      title: 'Phone (last 4)',
      dataIndex: 'lastdigitsofphone',
      key: 'phone',
    },
    {
      title: 'Clinic',
      key: 'clinic',
      render: (r) => (r.customer && r.customer.name) || '-',
    },
    {
      title: 'Surgeon',
      key: 'surgeon',
      render: (r) => (r.surgeon && r.surgeon.name) || '-',
    },
  ]

  return (
    <Modal
      className={'filterModal'}
      title={'Send Message — from ' + constants.MOVEAI_SUPPORT_NAME}
      visible={props.visible}
      closable={false}
      maskClosable={false}
      onCancel={handleCancel}
      width={'70%'}
      footer={[
        <Button key='cancel' onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key='send'
          type='primary'
          loading={sending}
          disabled={!selected}
          onClick={handleSend}
        >
          Send
        </Button>,
      ]}
    >
      <Card bordered={false}>
        <Row style={{ marginBottom: 12 }}>
          <Input.Search
            placeholder='Search patient by last name, last 4 digits, or full phone number'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
            enterButton='Search'
            loading={searching}
            allowClear
          />
        </Row>
        {results != null && (
          <Table
            size='small'
            columns={columns}
            dataSource={results.map((p) => ({ ...p, key: p._id }))}
            loading={searching}
            pagination={{ pageSize: 5, hideOnSinglePage: true }}
            locale={{ emptyText: 'No matching patients' }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selected ? [selected._id] : [],
              onChange: (keys, rows) => setSelected(rows[0]),
            }}
            onRow={(record) => ({
              onClick: () => setSelected(record),
            })}
            style={{ marginBottom: 16 }}
          />
        )}
        {selected && (
          <>
            <Row style={{ marginBottom: 12, fontWeight: 600 }}>
              {'To: ' +
                _.upperFirst(selected.firstname) +
                ' ' +
                _.upperFirst(selected.lastname) +
                '  —  Clinic: ' +
                ((selected.customer && selected.customer.name) || '-')}
            </Row>
            <Form form={form} layout={'vertical'}>
              <Form.Item
                name='subject'
                label='Subject'
                rules={[{ required: true, message: 'Please enter a Subject!' }]}
              >
                <Input.TextArea allowClear rows={1} />
              </Form.Item>
              <Form.Item
                name='message'
                label='Message'
                rules={[{ required: true, message: 'Please enter a Message!' }]}
              >
                <Input.TextArea allowClear rows={4} />
              </Form.Item>
            </Form>
          </>
        )}
      </Card>
    </Modal>
  )
}

const mapStateToProps = (state) => {
  return {
    companyid: state.profile.company?._id,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    createNewCase: (input) => dispatch(createNewCase(input)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MoveAIMessageModal)
